import { useCallback, useMemo } from "react"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import { FRIENDLY_DECIMALS_AMOUNT } from "#/shared/constants"
import { SwappableToken } from "#/shared/types"
import {
  bigIntToFloat,
  getMezoTokenAddress,
  getSwapRoutes,
  isStablePool,
  separateThousandsWithComma,
} from "#/shared/utils"

import useSwapAmountsIn from "./useSwapAmountsIn"
import useSwapAmountsOut from "./useSwapAmountsOut"

const useSwapRate = (tokenIn: SwappableToken, tokenOut: SwappableToken) => {
  const { getAmountsOut } = useSwapAmountsOut()
  const { getAmountsIn } = useSwapAmountsIn()

  const isStable = useMemo(
    () => isStablePool(tokenIn, tokenOut),
    [tokenIn, tokenOut],
  )

  const tokenInDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenIn),
  )

  const tokenOutDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenOut),
  )

  const getSwapAmountOut = useCallback(
    async (amount: bigint) => {
      const fromTokenAddress = getMezoTokenAddress(tokenIn)
      const toTokenAddress = getMezoTokenAddress(tokenOut)

      const amountsOut = await getAmountsOut(
        amount,
        getSwapRoutes(fromTokenAddress, toTokenAddress, isStable),
      )

      if (!amountsOut) return [0n, 0n]

      return amountsOut
    },
    [getAmountsOut, tokenIn, tokenOut, isStable],
  )

  const getSwapAmountIn = useCallback(
    async (amount: bigint) => {
      const fromTokenAddress = getMezoTokenAddress(tokenIn)
      const toTokenAddress = getMezoTokenAddress(tokenOut)

      const amountIn = await getAmountsIn(
        amount,
        getSwapRoutes(fromTokenAddress, toTokenAddress, isStable),
      )

      if (!amountIn) return [0n, 0n]

      return amountIn
    },
    [tokenOut, tokenIn, getAmountsIn, isStable],
  )

  const getSwapRate = useCallback(
    async (amount: bigint) => {
      const [_, amountOut] = await getSwapAmountOut(amount)

      const integerRetrievedAmount = Number(
        bigIntToFloat(amountOut, tokenOutDecimals, tokenOutDecimals),
      )

      const integerAmountIn = Number(
        bigIntToFloat(amount, tokenInDecimals, tokenInDecimals),
      )

      if (!integerRetrievedAmount || !integerAmountIn) return null

      const rate = integerRetrievedAmount / integerAmountIn

      return separateThousandsWithComma(rate, 0, FRIENDLY_DECIMALS_AMOUNT)
    },
    [getSwapAmountOut, tokenInDecimals, tokenOutDecimals],
  )

  return { getSwapAmountOut, getSwapAmountIn, getSwapRate }
}

export default useSwapRate
